import z from "zod";
import { REGEX_PATTERNS, SUPPLEMENT_ADDITIONAL_ATTR } from "@/constants/constants";

// Create dynamic additional attributes schema based on constants
const createAdditionalAttributesSchema = () => {
  const schemaObject: Record<string, z.ZodString | z.ZodOptional<z.ZodString>> = {};

  SUPPLEMENT_ADDITIONAL_ATTR.forEach(attr => {
    let validation: z.ZodString | z.ZodOptional<z.ZodString>;

    if (attr.mandatory) {
      // Create required field
      validation = z.string().min(1, `${attr.label} is required`);
    } else {
      // Create optional field
      validation = z.string().optional();
    }

    // Add type-specific validation
    if (attr.type === "phone") {
      if (attr.mandatory) {
        validation = (validation as z.ZodString).refine((val) => REGEX_PATTERNS.PHONE.test(val), {
          message: "Phone number must start with + and include international code"
        });
      } else {
        validation = (validation as z.ZodOptional<z.ZodString>).refine((val) => !val || val.trim() === '' || REGEX_PATTERNS.PHONE.test(val), {
          message: "Phone number must start with + and include international code"
        });
      }
    } else if (attr.type === "email") {
      if (attr.mandatory) {
        validation = (validation as z.ZodString).refine((val) => REGEX_PATTERNS.EMAIL.test(val), {
          message: "Please enter a valid email address"
        });
      } else {
        validation = (validation as z.ZodOptional<z.ZodString>).refine((val) => !val || val.trim() === '' || REGEX_PATTERNS.EMAIL.test(val), {
          message: "Please enter a valid email address"
        });
      }
    }

    schemaObject[attr.key] = validation;
  });

  return z.object(schemaObject);
};

export const SupplementDataFormSchema = z.object({
  id: z.coerce.number<number>().optional(),
  name: z.string().min(2).max(100),
  type: z.string().min(2).max(100),
  descr: z.string().max(1000).optional(),
  active: z.boolean().optional(),
  addDetails: z.string().optional(),
  additionalAttributes: createAdditionalAttributesSchema().optional(),
});

// Validation function for additional attributes
export const validateAdditionalAttributes = (
  attributes: Record<string, string>,
  selectedType: string
): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  // Get relevant attributes for the selected type from constants
  const relevantAttributes = SUPPLEMENT_ADDITIONAL_ATTR.filter(
    (attr) => attr.value === selectedType
  );

  relevantAttributes.forEach((attr) => {
    const value = attributes[attr.key];

    // Check mandatory fields
    if (attr.mandatory && (!value || !value.trim())) {
      errors.push(`${attr.label} is required`);
      return;
    }

    // Validate if value is provided
    if (value && value.trim()) {
      if (attr.type === "phone" && !REGEX_PATTERNS.PHONE.test(value.trim())) {
        errors.push("Phone number must start with + and include international code");
      } else if (attr.type === "email" && !REGEX_PATTERNS.EMAIL.test(value.trim())) {
        errors.push("Please enter a valid email address");
      }
    }
  });

  return {
    isValid: errors.length === 0,
    errors
  };
};